import axios from "axios";
import { getCurrentSessionIdToken } from "../queries/AuthToken";
import { Answer } from "../types/answer";
import { ReviewAnswer, ReviewAnswerUpdate } from "../types/reviewAnswer";
import { QuestionType } from "../types/checklist";
import { errorToast, successToast } from "@idwal/idwal-react-components";
import { assertJsonResponse, inspectionApiUrl } from "../utils/serviceHelper";

const getInspectionApiHeaders = async () => {
    const token = await getCurrentSessionIdToken();
    return {
        "Content-Type": "application/json",
        "Authorization": 'Bearer ' + token
    };
};

/**
 * The surveyor's original submission, from the answers table. This
 * is the untouched as-synced data — tech-review edits live in the
 * inspection table which is handled by getReviewAnswers
 */
export const getSurveyorAnswers = async (inspectionId: string): Promise<Answer[] | undefined> => {
    // URL and token resolution sit inside the try so a misconfigured base URL or
    // an expired session takes the same reported path as a failed request,
    // rather than rejecting past the caller's error handling.
    try {
        const url = inspectionApiUrl(`/inspections/${inspectionId}`);
        const headers = await getInspectionApiHeaders();

        const response = await axios.get(url, { headers });
        assertJsonResponse(response, url);

        if (response.data?.answers) {
            // Transform object keyed by questionId to array
            const answersObj = response.data.answers;
            return Object.entries(answersObj).map(([questionId, answerData]): Answer => {
                const data = answerData as Record<string, unknown>;
                return {
                    answerId: questionId,
                    inspectionId,
                    questionId,
                    questionText: data.questionText as string | null,
                    value: data.value,
                    dataDictionaryId: data.dataDictionaryId as string | null,
                    createdAt: data.timestamp as string | null,
                    updatedAt: data.syncedAt as string | null,
                    questionType: data.questionType as QuestionType | null | undefined,
                    isNotable: data.isNotable as boolean | undefined,
                    inspectionNumber: data.inspectionNumber,
                };
            });
        }
    } catch (e) {
        console.error(`Failed to get answers for inspection ${inspectionId}:`, e);
        errorToast(`Failed to get answers for inspection: ${inspectionId}`);
    }
};

export const getInspectionRecord = async (inspectionId: string) => {
    // includeAnswers=false: we only want the inspection record here (vessel,
    // checklistType/version, status). The review answers come from the separate
    // review-answers endpoint.
    try {
        const url = inspectionApiUrl(`/inspections/${inspectionId}?includeAnswers=false`);
        const headers = await getInspectionApiHeaders();

        const response = await axios.get(url, { headers });
        // A 200 carrying anything but JSON means we never reached the API. Left
        // unchecked it reads as an inspection-shaped blank, and the page then
        // reports the inspection as missing when it is only unreachable.
        assertJsonResponse(response, url);

        if (response.data?.inspection) {
            const inspection = response.data.inspection;
            // Mirror getInspectionsList: expose inspectionId as `id` too. Keep the
            // asset-manager `images` block (photos grouped by question) — fall back
            // to the top level in case the backend returns it as a sibling.
            return { ...inspection, id: inspectionId, images: inspection.images ?? response.data.images };
        }

        // JSON, but no inspection in it — the backend 404s a genuinely missing
        // record, so this shape is unexpected and worth surfacing rather than
        // quietly returning undefined.
        throw new Error(`No inspection in the response from ${url}`);
    } catch (e) {
        console.error(`Failed to get inspection ${inspectionId}:`, e);
        errorToast(`Failed to get inspection: ${inspectionId}`);
    }
};

/**
 * The checklist allocation for the header info card. Quiet on purpose:
 * inspections that predate the config-driven pipeline have no record here,
 * and a missing allocation just renders as a blank row — not an error.
 */
export const getInspectionChecklistInfo = async (
    inspectionId: string
): Promise<{ checklistType: string | null; checklistVersion: string | null } | undefined> => {
    try {
        const url = inspectionApiUrl(`/inspections/${inspectionId}?includeAnswers=false`);
        const headers = await getInspectionApiHeaders();

        const response = await axios.get(url, { headers });
        assertJsonResponse(response, url);

        const inspection = response.data?.inspection;
        if (!inspection) {
            return undefined;
        }

        return {
            checklistType: inspection.checklistType ?? null,
            checklistVersion: inspection.checklistVersion ?? null,
        };
    } catch (e: any) {
        // A missing record (pre-config-driven inspection) is expected; anything
        // else still degrades to a blank row but should leave a trace.
        if (e?.response?.status !== 404) {
            console.warn(`Checklist info unavailable for inspection ${inspectionId}:`, e?.message ?? e);
        }
        return undefined;
    }
};

export const getReviewAnswers = async (inspectionId: string): Promise<ReviewAnswer[] | undefined> => {
    try {
        const url = inspectionApiUrl(`/inspections/${inspectionId}/review-answers`);
        const headers = await getInspectionApiHeaders();

        const response = await axios.get(url, { headers });
        assertJsonResponse(response, url);

        if (response.data?.answers) {
            return response.data.answers as ReviewAnswer[];
        }
    } catch (e) {
        console.error(`Failed to get review answers for inspection ${inspectionId}:`, e);
        errorToast(`Failed to get review answers for inspection: ${inspectionId}`);
    }
};

/**
 * Update a reviewed answer in the inspection table. `clears` carries the
 * questionNames of descendant answers orphaned by this edit; the backend
 * hard-deletes them in the same transaction. For a multi-choice question,
 * `optionIds` is its full option-id set so the backend can delete the deselected
 * option rows by exact key.
 */
export const updateReviewAnswer = async (
    inspectionId: string,
    update: ReviewAnswerUpdate,
    clears: string[] = [],
    optionIds?: string[]
): Promise<boolean> => {
    try {
        const url = inspectionApiUrl(`/inspections/${inspectionId}/review-answers`);
        const headers = await getInspectionApiHeaders();

        const body = optionIds ? { ...update, clears, optionIds } : { ...update, clears };
        const response = await axios.post(url, body, { headers });
        return response.status === 200;
    } catch (e) {
        console.error(`Failed to update response for inspection ${inspectionId}:`, e);
        errorToast("Failed to update response");
        return false;
    }
};

/**
 * Manually (re)trigger the EEXI/CII calculators for an inspection — they
 * re-read the reviewed answers, so a reviewer can re-run after edits.
 * Reviewer/admin only; the backend rejects other callers.
 */
export const triggerEmissionsCalculation = async (inspectionId: string): Promise<boolean> => {
    try {
        const url = inspectionApiUrl(`/inspections/${inspectionId}/calculate-emissions`);
        const headers = await getInspectionApiHeaders();

        const response = await axios.post(url, {}, { headers });
        if (response.status === 200) {
            successToast("EEXI/CII calculation triggered");
            return true;
        }
        errorToast("Failed to trigger the EEXI/CII calculation");
        return false;
    } catch (e) {
        console.error(`Failed to trigger the EEXI/CII calculation for inspection ${inspectionId}:`, e);
        errorToast("Failed to trigger the EEXI/CII calculation");
        return false;
    }
};

export const getInspectionsList = async () => {
    try {
        const url = inspectionApiUrl(`/inspections`);
        const headers = await getInspectionApiHeaders();

        const response = await axios.get(url, { headers });
        assertJsonResponse(response, url);

        if (response.data?.inspections) {
            // Map inspectionId to id for compatibility with existing Inspection type
            return response.data.inspections.map((inspection: Record<string, unknown>) => ({
                ...inspection,
                id: inspection.inspectionId,
            }));
        }
    } catch (e) {
        console.error("Failed to get inspections list:", e);
        errorToast("Failed to get inspections list");
    }
};
